import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { ArrowLeft, ShieldCheck, EnvelopeSimple, Phone, ArrowUpRight, X, Sparkle } from "@phosphor-icons/react";

const STATUSES = ["new", "contacted", "closed", "lost"];
const PAID_PLANS = [
  { id: "starter", label: "Starter" },
  { id: "pro", label: "Pro" },
  { id: "team", label: "Team" },
];

export default function AdminLeads() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [convertLead, setConvertLead] = useState(null);
  const [convertPlan, setConvertPlan] = useState("pro");
  const [convertNote, setConvertNote] = useState("");
  const [converting, setConverting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads");
      setLeads(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally { setLoading(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/leads/${id}`, { status });
      toast.success("Status updated");
      setLeads((cur) => cur.map((l) => l.id === id ? { ...l, status } : l));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const openConvert = (lead) => {
    setConvertLead(lead);
    // Default the plan dropdown to the plan the lead expressed interest in
    const interest = (lead.plan_interest || "").toLowerCase();
    if (PAID_PLANS.some(p => p.id === interest)) setConvertPlan(interest);
    else setConvertPlan("pro");
    setConvertNote("");
  };
  const closeConvert = () => { setConvertLead(null); setConvertNote(""); setConverting(false); };

  const submitConvert = async () => {
    if (!convertLead) return;
    setConverting(true);
    try {
      const r = await api.post(`/admin/leads/${convertLead.id}/convert`, { plan: convertPlan, note: convertNote });
      const email = r.data.email;
      const action = r.data.action;
      toast.success(action === "created_and_upgraded"
        ? `Account created for ${email} on ${convertPlan}`
        : `${email} upgraded to ${convertPlan}`);
      closeConvert();
      // Jump to the user in /admin/users, highlight the row
      nav(`/admin/users?highlight=${encodeURIComponent(email)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
      setConverting(false);
    }
  };

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const shown = filter === "all" ? leads : leads.filter(l => (l.status || "new") === filter);
  const countBy = (s) => leads.filter(l => (l.status || "new") === s).length;

  return (
    <div className="p-6 md:p-10" data-testid="admin-leads-page">
      <Toaster theme="dark" />
      <div className="mb-8">
        <Link to="/dashboard" className="overline flex items-center gap-1 hover:text-white mb-2"><ArrowLeft weight="bold" size={12} /> back</Link>
        <div className="overline mb-2 flex items-center gap-2"><ShieldCheck size={14} weight="fill" color="var(--accent)" /> // admin · leads inbox</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Book-a-call requests</h1>
        <p className="text-sm mt-3" style={{ color: "var(--text-2)" }}>Everyone who submitted the Book-a-call form. Track outreach status; the primary notification also lands in your inbox.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-leads-filter">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>All · {leads.length}</FilterChip>
        {STATUSES.map(s => (
          <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)}>
            {s.toUpperCase()} · {countBy(s)}
          </FilterChip>
        ))}
      </div>

      <div className="border" style={{ borderColor: "var(--border)" }}>
        {loading && <div className="p-6 text-sm text-[#C7DDE5]">Loading leads…</div>}
        {!loading && shown.length === 0 && <div className="p-6 text-sm text-[#C7DDE5]">No leads in this bucket yet.</div>}
        {shown.map((l, i) => (
          <div key={l.id} data-testid={`admin-lead-row-${i}`} className={`p-5 ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg">{l.name}</div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm" style={{ color: "var(--text-2)" }}>
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-white"><EnvelopeSimple size={14} weight="bold" /> {l.email}</a>
                  {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-white"><Phone size={14} weight="bold" /> {l.phone}</a>}
                  {l.company && <span>· {l.company}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>{(l.plan_interest || "inquiry").toUpperCase()}</span>
                <select
                  value={l.status || "new"}
                  onChange={(e) => setStatus(l.id, e.target.value)}
                  data-testid={`admin-lead-status-${i}`}
                  className="input-base"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "6px 10px", fontSize: 12 }}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {(l.best_time || l.message) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {l.best_time && <Field label="Best time">{l.best_time}</Field>}
                {l.message && <Field label="Message">{l.message}</Field>}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="overline">
                received {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                {l.converted_plan && (
                  <span className="ml-3" style={{ color: "var(--accent)" }}>
                    · converted → {l.converted_plan.toUpperCase()}
                  </span>
                )}
              </div>
              {l.converted_to_user_id ? (
                <Link
                  to={`/admin/users?highlight=${encodeURIComponent(l.email)}`}
                  data-testid={`admin-lead-view-user-${i}`}
                  className="text-xs uppercase tracking-widest font-mono flex items-center gap-1 hover:text-white"
                  style={{ color: "var(--accent)" }}
                >
                  view account <ArrowUpRight weight="bold" />
                </Link>
              ) : (
                <button
                  onClick={() => openConvert(l)}
                  data-testid={`admin-lead-convert-${i}`}
                  className="btn-primary flex items-center gap-2 text-xs px-3 py-2"
                >
                  <Sparkle weight="bold" size={14} /> Convert to paid
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Convert dialog */}
      {convertLead && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
          data-testid="convert-lead-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md border relative" style={{ background: "var(--surface-1)", borderColor: "var(--accent)" }}>
            <button onClick={closeConvert} data-testid="convert-close-btn" className="absolute top-3 right-3 p-2 hover:bg-[#15303A]" aria-label="Close"><X weight="bold" /></button>
            <div className="p-8">
              <div className="overline mb-2" style={{ color: "var(--accent)" }}>// convert lead → paid customer</div>
              <h2 className="font-display font-black text-2xl tracking-tighter mb-1">{convertLead.name}</h2>
              <div className="text-sm mb-6" style={{ color: "var(--text-2)" }}>{convertLead.email}</div>

              <label className="label-base">Assign plan</label>
              <select
                value={convertPlan}
                onChange={(e) => setConvertPlan(e.target.value)}
                data-testid="convert-plan-select"
                className="input-base mb-4"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }}
              >
                {PAID_PLANS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>

              <label className="label-base">Note (included in welcome email — optional)</label>
              <textarea
                value={convertNote}
                onChange={(e) => setConvertNote(e.target.value)}
                data-testid="convert-note-input"
                rows={3}
                placeholder="Thanks for the call — here's your account…"
                className="input-base mb-6"
                style={{ border: "1px solid var(--border)", padding: "10px 12px" }}
              />

              <div className="border p-3 mb-6 text-xs" style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--text-2)" }}>
                If <b>{convertLead.email}</b> already has an account, we&apos;ll upgrade their plan.
                If not, we&apos;ll create their account on the chosen plan and email them a claim link (valid 14 days).
              </div>

              <div className="flex gap-3">
                <button
                  onClick={submitConvert}
                  disabled={converting}
                  data-testid="convert-submit-btn"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {converting ? "Converting…" : <><Sparkle weight="bold" /> Confirm conversion</>}
                </button>
                <button onClick={closeConvert} className="btn-secondary" data-testid="convert-cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="text-xs uppercase tracking-widest font-mono px-3 py-2 border transition"
      style={{
        borderColor: active ? "var(--accent)" : "var(--border)",
        color: active ? "var(--accent)" : "var(--text-2)",
        background: active ? "rgba(45,212,212,0.08)" : "transparent",
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="border p-3" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm whitespace-pre-wrap">{children}</div>
    </div>
  );
}
